import { useMemo } from 'react'
import moment from 'moment'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { EmptyObject } from 'lib/object/objectUtils'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SearchTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/SearchTileFlightOnlyPricing'
import SearchTileWalledMessage from '../shared/SearchTileWalledMessage'
import WalledContent from 'components/Common/WalledContent'
import { calculateOfferTileDiscountPercentage } from 'lib/search/searchUtils'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isFlashHotelOffer, isLEHotel } from 'lib/offer/offerTypes'
import { MALDIVES_BUNDLE_DEFAULT_GUESTS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOffer from 'hooks/Offers/useOffer'
import { isRegionFilterBypassedForOffer } from 'selectors/marketingSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import CharterBundlePricing from './CharterBundlePricing'
import { getCharterBundleSearchTilePricing } from './charterBundleSearchTilePricing'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'
import { getLowestPriceMinDurationPackage } from 'lib/offer/getLowestPricePackage'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  bestRate?: App.OfferAvailableRate
  duration?: number
  hidePrice?: boolean
  showPerNightPricing?: boolean
  hideIncludesTaxesAndFees?: boolean
  disableCharterPricing?: boolean
  showCompactPricing?: boolean
  compactPriceSize?: 'S' | 'M' | 'L'
  pricingAlign?: 'start' | 'end'
  hideMemberWalls?: boolean
}

function HotelSearchTilePriceStack({
  bestPackage,
  filters,
  offer,
  bestRate,
  duration,
  hidePrice = false,
  showPerNightPricing = false,
  hideIncludesTaxesAndFees = false,
  disableCharterPricing = false,
  showCompactPricing = false,
  compactPriceSize = 'L',
  pricingAlign = 'start',
  hideMemberWalls = false,
}: Props) {
  const hasDates = !!(filters?.checkIn && filters?.checkOut && filters?.rooms?.length)
  const hasSearchedStayDuration = !!((filters?.checkIn && filters?.checkOut) || filters?.flexibleNights)
  const checkInDate = useMemo(() => {
    return filters?.checkIn ? moment(filters.checkIn) : undefined
  }, [filters?.checkIn])

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? EmptyObject, offerId: offer.id })
  const minimumDurationPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!isFlashHotelOffer(offer) || !offer.minDuration) {
      return undefined
    }

    return getLowestPriceMinDurationPackage(offer, offer.packages, offer.minDuration)
  }, [offer])
  const shouldUseAgentHubFlashOfferMinStayPricing = isAgentHubEnabled && isFlashHotelOffer(offer) && !!minimumDurationPackage && !hasSearchedStayDuration

  // Anytime search (no dates/flexible nights) with region filter bypassed can't rely on the
  // search stream's single lowest price. Source the full offer from public-offer instead, so we
  // can price from the lowest-price package at the offer's minimum length of stay — selected the
  // same way the offer page selects it, so the tile can't lead with a price the PDP won't honour.
  const isAnytimeSearch = !filters?.checkIn && !filters?.checkOut && !filters?.flexibleNights
  const bypassRegionFilter = useAppSelector(state => isRegionFilterBypassedForOffer(state, offer.id))
  const shouldUsePublicOfferMinStayPricing = isAnytimeSearch
    && bypassRegionFilter
    && isLEHotel(offer)
  const publicOfferAlreadyFetched = useAppSelector((state) => !!state.offer.offersBypassRegion?.[offer.id])
  const [publicOffer] = useOffer<App.HotelOffer>(offer.id, {
    disabled: !shouldUsePublicOfferMinStayPricing,
    bypassRegionFilter: true,
  })
  const publicOfferMinDurationPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!shouldUsePublicOfferMinStayPricing || !publicOffer?.minDuration || !publicOffer.packages?.length) {
      return undefined
    }

    return getLowestPriceMinDurationPackage(publicOffer, publicOffer.packages, publicOffer.minDuration)
  }, [shouldUsePublicOfferMinStayPricing, publicOffer])
  // Guard with publicOfferAlreadyFetched: publicOffer is sourced from getAllOffers which includes
  // the search-stream summary (which has packages). Without this guard, applyPublicOfferMinStayPricing
  // becomes true immediately from the summary's packages before the bypass fetch completes.
  const applyPublicOfferMinStayPricing = shouldUsePublicOfferMinStayPricing && publicOfferAlreadyFetched && !!publicOfferMinDurationPackage

  const pricingPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (shouldUseAgentHubFlashOfferMinStayPricing) {
      return minimumDurationPackage
    }
    if (applyPublicOfferMinStayPricing) {
      return publicOfferMinDurationPackage
    }
    if (shouldUsePublicOfferMinStayPricing && !publicOfferAlreadyFetched) {
      // Show loading indicator rather than flash the search-stream price while
      // the bypass-region offer is in flight.
      return undefined
    }
    return bestPackage
  }, [shouldUseAgentHubFlashOfferMinStayPricing, minimumDurationPackage, applyPublicOfferMinStayPricing, publicOfferMinDurationPackage, shouldUsePublicOfferMinStayPricing, publicOfferAlreadyFetched, bestPackage])
  const oneClickBookingRestorationPackagePricing = isAgentHubEnabled ? undefined : filters?.oneClickBookingRestoration?.packagePricing

  const availableRate = useMemo<App.OfferAvailableRate | undefined>(() => {
    if (oneClickBookingRestorationPackagePricing && pricingPackage) {
      // One-click booking restoration has already validated and priced the exact checkout cart package.
      // OfferTilePricing accepts package-specific live totals through availableRate, so adapt the
      // restoration totals into that shape instead of letting the normal best-rate path replace them.
      const rooms = filters?.rooms ?? []
      const packageSurcharge = pricingPackage.surcharge ?? 0
      const extraGuestSurcharge = getSumExtraGuestsSurcharge(rooms, offer, pricingPackage)
      const price = Math.max(oneClickBookingRestorationPackagePricing.price + oneClickBookingRestorationPackagePricing.surcharge - packageSurcharge - extraGuestSurcharge, 0)
      const memberPriceWithSurcharge = Math.max(oneClickBookingRestorationPackagePricing.memberPrice + oneClickBookingRestorationPackagePricing.surcharge - extraGuestSurcharge, 0)

      return {
        offerId: offer.id,
        price,
        memberPrice: oneClickBookingRestorationPackagePricing.memberPrice,
        memberPriceWithSurcharge,
        surcharge: oneClickBookingRestorationPackagePricing.surcharge,
        hotelPrice: oneClickBookingRestorationPackagePricing.price,
        hotelMemberPrice: oneClickBookingRestorationPackagePricing.memberPrice,
        hotelValue: oneClickBookingRestorationPackagePricing.value,
        hotelMemberValue: oneClickBookingRestorationPackagePricing.memberValue,
        // Flash restoration is hotel-only, so there is no bundled flight component to add.
        flightPrice: 0,
        extraGuestSurcharge: oneClickBookingRestorationPackagePricing.extraGuestSurcharge,
        taxesAndFees: oneClickBookingRestorationPackagePricing.taxesAndFees,
        propertyFees: oneClickBookingRestorationPackagePricing.propertyFees,
        duration: pricingPackage.duration,
        packageId: pricingPackage.id,
        packageUniqueKey: pricingPackage.uniqueKey,
        roomRateId: pricingPackage.roomRate.id,
        // Required by App.OfferAvailableRate, but not read by this search-tile pricing path.
        availableRooms: 1,
        hasTactical: false,
      }
    }

    if (shouldUseAgentHubFlashOfferMinStayPricing || applyPublicOfferMinStayPricing) {
      // These tiles intentionally price from the minimum-stay package, not a live rate.
      return undefined
    }

    return bestRate
  }, [bestRate, filters?.rooms, offer, oneClickBookingRestorationPackagePricing, pricingPackage, shouldUseAgentHubFlashOfferMinStayPricing, applyPublicOfferMinStayPricing])

  const pricingSearchPrices = shouldUseAgentHubFlashOfferMinStayPricing || applyPublicOfferMinStayPricing ? undefined : directSearchPrices
  const pricingFilters = useMemo(() => {
    if ((!shouldUseAgentHubFlashOfferMinStayPricing && !applyPublicOfferMinStayPricing) || !filters || !pricingPackage?.roomRate?.id || !pricingPackage.roomType?.id) {
      return filters
    }

    return {
      ...filters,
      roomRateId: pricingPackage.roomRate.id,
      roomTypeId: pricingPackage.roomType.id,
    }
  }, [filters, pricingPackage, shouldUseAgentHubFlashOfferMinStayPricing, applyPublicOfferMinStayPricing])
  const discountPercentage = useMemo(() => {
    if (offer.isDiscountPillHidden) {
      return undefined
    }

    if (pricingSearchPrices?.lowestPrice && pricingSearchPrices?.lowestPriceValue) {
      return calculateOfferTileDiscountPercentage({
        price: pricingSearchPrices.lowestPrice,
        value: pricingSearchPrices.lowestPriceValue,
      })
    }

    if (oneClickBookingRestorationPackagePricing) {
      return calculateOfferTileDiscountPercentage({
        price: oneClickBookingRestorationPackagePricing.price + oneClickBookingRestorationPackagePricing.surcharge,
        value: oneClickBookingRestorationPackagePricing.value,
      })
    }

    if (pricingPackage) {
      return calculateOfferTileDiscountPercentage({
        price: pricingPackage.price,
        value: pricingPackage.value,
      })
    }

    return undefined
  }, [offer, oneClickBookingRestorationPackagePricing, pricingPackage, pricingSearchPrices])

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const { isPricePerPerson, duration: contextDuration } = useMaldivesDirectBundleContext()

  const isCharterBundleOffer = isFlashHotelOffer(offer) &&
    offer.offerFlightsEnabled &&
    offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const charterSearchPrices = offerMetaData?.pricing
  const charterPricing = getCharterBundleSearchTilePricing({
    charterSearchPrices,
  })

  if (!disableCharterPricing && isCharterBundleOffer && isDisplayablePrice(charterPricing?.price)) {
    const totalGuests = filters?.rooms ? sumUpOccupancies(filters.rooms) : MALDIVES_BUNDLE_DEFAULT_GUESTS
    return <Group direction="vertical" gap={4} horizontalAlign={pricingAlign}>
      <LuxPlusOfferVoucherCallout
        offer={offer}
        mode="search"
        align="start"
      />
      <CharterBundlePricing
        offer={offer}
        pricing={{
          price: charterPricing.price,
          memberPrice: charterPricing.memberPrice,
          value: charterPricing.value,
          discountPercent: charterPricing.discountPercent,
          memberDiscountPercent: charterPricing.memberDiscountPercent,
          totalGuests,
          roomCount: filters?.rooms?.length,
        }}
        isPricePerPerson={isPricePerPerson}
        nights={contextDuration ?? charterSearchPrices?.duration ?? duration}
        filters={filters}
        hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
        hideReturnFareFree
      />
    </Group>
  }

  const hasPricing = hasDisplayablePackagePrice(pricingPackage) ||
    isDisplayablePrice(availableRate?.price) ||
    isDisplayablePrice(pricingSearchPrices?.lowestPrice)

  if (!pricingPackage) {
    return <LoadingIndicator inline ballSize={12} />
  }

  if (!hasPricing) return null

  return <div>
    <WalledContent
      enforced={!hideMemberWalls && offer.walledGarden}
      wall={<SearchTileWalledMessage discountPercentage={discountPercentage} />}
    >
      <Group direction="vertical" gap={8} fullWidth>
        {!offer.bundledWithFlightsOnly && <OfferTilePricing
          isSearchTile
          offer={offer}
          hasDates={hasDates}
          checkInDate={checkInDate}
          pkg={pricingPackage}
          availableRate={availableRate}
          align={pricingAlign}
          filters={pricingFilters}
          duration={shouldUseAgentHubFlashOfferMinStayPricing || applyPublicOfferMinStayPricing ? pricingPackage.duration : duration}
          hidePrice={hidePrice}
          overrideRoomsNumber={offerMetaData?.rooms?.length}
          isPricePerNight={showPerNightPricing}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
          compactExperimentLayout={showCompactPricing}
          compactPriceSize={compactPriceSize}
        />}
        {(offer.bundledWithFlightsOnly && !hidePrice) && (
          <SearchTileFlightOnlyPricing
            offer={offer}
            rooms={filters?.rooms}
            hasDates={hasDates}
            pkg={pricingPackage}
            pricing={bestRate}
            filters={filters}
          />
        )}
      </Group>
    </WalledContent>
  </div>
}

export default HotelSearchTilePriceStack
